import { ref } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Zona del teatro: la caducidad se guarda siempre en hora de Madrid. */
const MADRID_TIME_ZONE = 'Europe/Madrid';

/**
 * useEnviarInvitaciones — estado y acciones del modal de envío de invitaciones.
 *
 * El modal sólo pinta; acá vive lo que se puede romper: validación de correos,
 * payload por pedido y formato de la fecha de caducidad. `enviar` es inyectable
 * para poder testear sin tocar el store.
 *
 * @param {Object} options
 * @param {Array|import('vue').Ref<Array>} options.orders Pedidos generados.
 * @param {number|string} options.eventoId
 * @param {number|string} options.sesionId
 * @param {Function} [options.enviar] Inyectable: recibe el payload de un pedido.
 * @returns {Object} Estado y acciones del envío.
 */
export function useEnviarInvitaciones({ orders, eventoId, sesionId, enviar } = {}) {
    const unwrap = value =>
        value && typeof value === 'object' && 'value' in value ? value.value : value;

    const errores = ref({});
    const loading = ref(false);
    const caducidad_entradas = ref(false);
    const fecha_caducidad = ref(null);
    const fechaError = ref(null);

    async function dispatchEnviar(payload) {
        return store.dispatch(
            config.getVuexAlias('back.events.sesiones.entradas.enviar.email'),
            payload
        );
    }

    const doEnviar = enviar || dispatchEnviar;

    // Los correos se editan en local: el array de pedidos es del padre y no se
    // muta (mutar props rompe el flujo de datos y dispara avisos de Vue).
    // Se inicializan de forma SÍNCRONA: el modal se monta con los pedidos ya
    // generados, así que no hace falta depender de un watcher (que además
    // trataría un array plano como varias fuentes).
    function emailsDesdeOrders(list) {
        const next = {};
        (unwrap(list) || []).forEach(order => {
            next[order.id] = order.email_comprador || '';
        });
        return next;
    }

    const emails = ref(emailsDesdeOrders(orders));

    /** Asigna el correo de un pedido sin mutar props. */
    function setEmail(id, value) {
        emails.value = { ...emails.value, [id]: value };
    }

    /**
     * `datetime-local` devuelve "YYYY-MM-DDTHH:mm" en la hora LOCAL DEL NAVEGADOR
     * y sin offset. El backend guarda en una columna `timestamp`, que interpreta
     * el valor en la zona de la sesión de base de datos (Europe/Madrid): mandarlo
     * tal cual desplaza la caducidad si quien administra está en otra zona.
     *
     * Se resuelve el instante elegido y se formatea en hora de Madrid, que es la
     * zona del teatro y la que esperaba el control anterior (`value-zone`).
     *
     * @param {string|null} value Valor del `<input type="datetime-local">`.
     * @returns {string|null} "YYYY-MM-DD HH:mm:ss" en Europe/Madrid.
     */
    function fechaParaBackend(value) {
        if (!value) return null;

        const instante = new Date(String(value).replace(' ', 'T'));
        if (Number.isNaN(instante.getTime())) return null;

        const partes = new Intl.DateTimeFormat('en-GB', {
            timeZone: MADRID_TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
        }).formatToParts(instante);

        const valor = tipo => (partes.find(parte => parte.type === tipo) || {}).value;

        return `${valor('year')}-${valor('month')}-${valor('day')} ${valor('hour')}:${valor('minute')}:${valor('second')}`;
    }

    /** Valida correos y fecha. Devuelve false y publica los errores si algo falla. */
    function validar() {
        const list = unwrap(orders) || [];
        const next = {};

        list.forEach(order => {
            if (!EMAIL_RE.test((emails.value[order.id] || '').trim())) {
                next[order.id] = 'Introduce un correo electrónico válido';
            }
        });

        errores.value = next;
        fechaError.value = caducidad_entradas.value && !fecha_caducidad.value
            ? 'Indica la fecha de caducidad'
            : null;

        return Object.keys(next).length === 0 && !fechaError.value;
    }

    /**
     * Envía las invitaciones de todos los pedidos.
     * @returns {Promise<{ok: boolean, message: (string|null), error?: unknown}>}
     */
    async function enviarInvitaciones() {
        const list = unwrap(orders) || [];

        if (loading.value || !validar()) {
            return { ok: false, message: null };
        }

        loading.value = true;

        try {
            const respuestas = await Promise.all(
                list.map(order =>
                    doEnviar({
                        evento_id: unwrap(eventoId),
                        sesion_id: unwrap(sesionId),
                        user_order: order.id,
                        qr_codes: order.qr_codes,
                        email: (emails.value[order.id] || '').trim(),
                        caducidad_entradas: caducidad_entradas.value,
                        fecha_caducidad: fechaParaBackend(fecha_caducidad.value)
                    })
                )
            );

            const message = respuestas[0] && respuestas[0].data
                ? respuestas[0].data.message
                : null;

            return { ok: true, message };
        } catch (error) {
            return { ok: false, message: null, error };
        } finally {
            loading.value = false;
        }
    }

    return {
        emails,
        errores,
        loading,
        caducidad_entradas,
        fecha_caducidad,
        fechaError,
        setEmail,
        validar,
        fechaParaBackend,
        enviarInvitaciones
    };
}
